// AI Service for handling API requests

const API_KEY = process.env.REACT_APP_NEXON_API_KEY;

if (!API_KEY) {
  console.error('API key is not set in environment variables');
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
}

export interface GeneratedImage {
  url: string;
  prompt: string;
  timestamp: Date;
}

export interface GeneratedVideo {
  url: string;
  prompt: string;
  timestamp: Date;
}

export interface GeneratedCode {
  code: string;
  language: string;
  prompt: string;
  timestamp: Date;
}

export interface GeneratedText {
  content: string;
  prompt: string;
  timestamp: Date;
}

const generateResponse = async (message: string): Promise<string> => {
  try {
    const response = await fetch(
      `https://api.nexon.my.id/api/chat-gpt?q=${encodeURIComponent(message)}&apikey=${API_KEY}`
    );

    if (!response.ok) {
      throw new Error('Failed to get response from Nexon API');
    }

    const data = await response.json();
    return data.data || 'No response from AI';
  } catch (error) {
    console.error('Error generating AI response:', error);
    if (error instanceof Error) {
      return `I apologize, but there was an error: ${error.message}. Please try again.`;
    }
    return "I apologize, but I'm having trouble connecting to the AI service. Please try again in a moment.";
  }
};

export const generateChat = async (messages: ChatMessage[]): Promise<string> => {
  try {
    const response = await fetch('https://api.nexon.my.id/api/chat/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messages.map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
        apikey: API_KEY,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate chat response');
    }

    const data = await response.json();
    if (data && data.status) {
      return data.message;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error generating chat response:', error);
    throw error;
  }
};

export const generateImage = async (prompt: string): Promise<GeneratedImage> => {
  try {
    const response = await fetch(
      `https://api.nexon.my.id/api/bing/createimage?q=${encodeURIComponent(prompt.trim())}&apikey=${API_KEY}`
    );

    if (!response.ok) {
      throw new Error('Failed to generate image');
    }

    const data = await response.json();
    if (data && data.status) {
      const result = data.data[Math.floor(Math.random() * data.data.length)];
      return {
        url: result.url,
        prompt: prompt.trim(),
        timestamp: new Date(),
      };
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error generating image:', error);
    throw error;
  }
};

export const generateVideo = async (prompt: string): Promise<GeneratedVideo> => {
  try {
    const response = await fetch(
      `https://api.nexon.my.id/api/video/generate?q=${encodeURIComponent(prompt.trim())}&apikey=${API_KEY}`
    );

    if (!response.ok) {
      throw new Error('Failed to generate video');
    }

    const data = await response.json();
    if (data && data.status) {
      return {
        url: data.url,
        prompt: prompt.trim(),
        timestamp: new Date(),
      };
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error generating video:', error);
    throw error;
  }
};

export const generateCode = async (prompt: string, language: string): Promise<GeneratedCode> => {
  try {
    const response = await fetch(
      `https://api.nexon.my.id/api/code/generate?q=${encodeURIComponent(prompt.trim())}&lang=${language}&apikey=${API_KEY}`
    );

    if (!response.ok) {
      throw new Error('Failed to generate code');
    }

    const data = await response.json();
    if (data && data.status) {
      return {
        code: data.code,
        language,
        prompt: prompt.trim(),
        timestamp: new Date(),
      };
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error generating code:', error);
    throw error;
  }
};

export const generateText = async (prompt: string): Promise<GeneratedText> => {
  try {
    const response = await fetch(
      `https://api.nexon.my.id/api/text/generate?q=${encodeURIComponent(prompt.trim())}&apikey=${API_KEY}`
    );

    if (!response.ok) {
      throw new Error('Failed to generate text');
    }

    const data = await response.json();
    if (data && data.status) {
      return {
        content: data.text,
        prompt: prompt.trim(),
        timestamp: new Date(),
      };
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Error generating text:', error);
    throw error;
  }
};

export const downloadMedia = async (url: string): Promise<Blob> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to download media');
    }
    return await response.blob();
  } catch (error) {
    console.error('Error downloading media:', error);
    throw error;
  }
};

export const aiService = {
  generateResponse,
  generateChat,
  generateImage,
  generateVideo,
  generateCode,
  generateText,
  downloadMedia,
};